import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl, publicMediaSrc } from "../config/api";
import { adminJson, getAdminSession, logoutAdmin } from "../utils/adminSession";

const nav = [
  { to: "/admin", label: "Dashboard", end: true, icon: HomeIcon },
  { to: "/admin-category", label: "Categories", icon: FolderIcon },
  { to: "/admin-products", label: "Products", icon: BoxIcon },
  { to: "/admin-deliveries", label: "Deliveries", icon: TruckIcon },
  { to: "/admin-form", label: "Form Submissions", icon: InboxIcon },
  { to: "/admin-contact", label: "Contact Information", icon: PhoneIcon },
  { to: "/admins", label: "Administrators", icon: PeopleIcon },
  { to: "/admin-activities", label: "Recent Activities", icon: ActivityIcon },
];

const titles = {
  "/admin": "Dashboard",
  "/admin-activities": "Recent Activities",
  "/admin-category": "Categories",
  "/admin-products": "Products",
  "/admin-deliveries": "Deliveries",
  "/admin-form": "Form Submissions",
  "/admin-contact": "Contact Information",
  "/admins": "Administrators",
};

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [adminName, setAdminName] = useState("");
  const session = getAdminSession();

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!session?.email) return undefined;
    let cancelled = false;
    adminJson("/admins")
      .then((list) => {
        if (cancelled) return;
        const current = Array.isArray(list)
          ? list.find((item) => String(item.email || "").toLowerCase() === String(session.email).toLowerCase())
          : null;
        setAdminName(String(current?.name || session.name || "").trim());
      })
      .catch(() => {
        if (!cancelled) setAdminName(String(session.name || "").trim());
      });
    return () => {
      cancelled = true;
    };
  }, [session?.email, session?.name]);

  const siteName = String(contact?.siteName || "").trim();

  useEffect(() => {
    if (!session) return undefined;
    document.title = siteName ? `Admin Portal | ${siteName}` : "Admin Portal";
    return undefined;
  }, [session, siteName]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  function onLogout() {
    logoutAdmin();
    navigate("/", { replace: true });
  }

  const pageTitle = titles[location.pathname] || "Admin";
  const logoSrc = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);
  const displayName = adminName || String(session.name || "").trim() || "Admin";
  const displayInitial = displayName.slice(0, 1).toUpperCase();

  return (
    <div className="flex min-h-screen overflow-x-hidden bg-neutral-100">
      {menuOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[232px] flex-col bg-at-darker text-white transition-transform lg:static lg:translate-x-0 ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col items-center px-5 pb-6 pt-7">
          <img
            src={logoSrc}
            alt=""
            className="h-12 w-12 object-contain invert"
          />
          <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-white/85">
            Admin Portal
          </p>
          {siteName ? (
            <p className="mt-1 text-center text-[9px] font-medium uppercase leading-relaxed tracking-[0.16em] text-white/45">
              {siteName}
            </p>
          ) : null}
        </div>

        <nav className="flex-1 space-y-0.5 px-3">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm ${
                  isActive
                    ? "bg-[#8a6a3e] text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto border-t border-white/10 px-3 py-4">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 hover:bg-white/5 hover:text-white"
          >
            <ExternalIcon />
            View Website
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-white/70 hover:bg-white/5 hover:text-white"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-[10px] font-semibold uppercase">
              {displayInitial}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-white/90">{displayName}</span>
              <span className="block text-xs text-white/40">Log out</span>
            </span>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
        <header className="flex min-w-0 items-center justify-between gap-2 border-b border-neutral-200 bg-white px-4 py-4 sm:gap-4 sm:px-8">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              className="shrink-0 rounded-md p-1.5 text-at-dark lg:hidden"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon />
            </button>
            <h2 className="truncate text-lg font-semibold text-at-dark sm:text-xl">{pageTitle}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {location.pathname === "/admin" ? (
              <Link
                to="/admin-products"
                className="inline-flex items-center rounded-md bg-at-dark px-2.5 py-2 text-xs text-white hover:bg-at-darker sm:px-3 sm:text-sm"
              >
                + Add Product
              </Link>
            ) : null}
            <Link
              to="/"
              className="hidden items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm text-at-dark hover:border-at-gold sm:inline-flex"
            >
              View Website
              <ExternalIcon className="h-3.5 w-3.5" />
            </Link>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 11.5L12 5l8 6.5V20H4z" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 12h4l2-6 4 12 2-6h4" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h6l2 2h8v10H4z" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 8l8-4 8 4-8 4z" />
      <path d="M4 8v8l8 4 8-4V8" />
      <path d="M12 12v8" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 7h11v9H3z" />
      <path d="M14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="17" r="1.4" />
      <circle cx="17" cy="17" r="1.4" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 6h16v12H4z" />
      <path d="M4 14h4l1.5 2h5L16 14h4" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="7" y="3" width="10" height="18" rx="1.5" />
      <path d="M11 18h2" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="8" r="3" />
      <path d="M4 19a5 5 0 0110 0" />
      <circle cx="16.5" cy="9" r="2.2" />
      <path d="M16 19a4.5 4.5 0 014-4.4" />
    </svg>
  );
}

function ExternalIcon({ className = "h-4 w-4 shrink-0" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 16L19 5" />
      <path d="M11 5h8v8" />
      <path d="M19 13v6H5V5h6" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export default AdminLayout;
